// PlaceGrid Component - Responsive Grid for Places

import React, { useMemo, useCallback } from 'react';
import type { Place } from '../../types';
import { PlaceCard } from './PlaceCard';
import { LoadingState } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';

interface PlaceGridProps {
  places: Place[];
  favorites: string[];
  loading?: boolean;
  onSelect: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
  emptyMessage?: string;
  emptyDescription?: string;
  userLocation?: { latitude: number; longitude: number } | null;
}

export const PlaceGrid = React.memo(function PlaceGrid({
  places,
  favorites,
  loading = false,
  onSelect,
  onToggleFavorite,
  emptyMessage,
  emptyDescription,
  userLocation,
}: PlaceGridProps) {
  // Memoize favorites Set for O(1) lookup instead of O(n) with includes()
  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

  // Check if a place is favorited (O(1) lookup)
  const isFavorite = useCallback(
    (placeId: string) => favoritesSet.has(placeId),
    [favoritesSet]
  );

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingState type="cards" count={8} />
      </div>
    );
  }

  if (places.length === 0) {
    return (
      <EmptyState
        type="search"
        title={emptyMessage}
        message={emptyDescription}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {places.map((place) => (
        <PlaceCard
          key={place.id}
          place={place}
          isFavorite={isFavorite(place.id)}
          onSelect={onSelect}
          onToggleFavorite={onToggleFavorite}
          userLocation={userLocation}
        />
      ))}
    </div>
  );
});

export default PlaceGrid;
